import { useEffect, useState } from "react";
import {
  FormText,
  Input,
  Label,
  UncontrolledAccordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
} from "reactstrap";
import { GoChevronDown } from "react-icons/go";
import { BsFileEarmarkArrowDown } from "react-icons/bs";
import { toast } from "react-toastify";
import { fetchApi } from "../utils/DatabaseHelper";
import { downloadCsv } from "../utils/ExportHelper";
import { dedupeMetaFields } from "../utils/metaFieldsHelper";
import { zeitraumVonPreset, zeitraumQueryFenster } from "../utils/exportZeitraum";
import CustomCheckbox from "./CustomFormFields/CustomCheckbox";

const SPEICHER_PREFIX = "csvExportDialog:";

const BEURTEILUNGEN = [
  "teilgenommen",
  "nicht teilgenommen",
  "geeignet",
  "nicht geeignet",
  "bedingt geeignet",
  "steht aus",
  "abgelehnt",
];

// Konfiguration pro Export-Endpunkt. 'keys' gehen als fields-Query ans
// Backend; das fixe Minimum (feste) ist im Backend nicht abwählbar.
// Default checked = aktueller Bestands-Export, damit sich am Output nichts
// ändert, nur weil jetzt der Dialog davor sitzt.
const ENDPUNKT_KONFIG = {
  // Vorsorgekartei-Übersicht: Zeilenmodell mit Zeitraum/Beurteilung
  "report/mitarbeiter_uebersicht": {
    feste: ["Nachname", "Vorname", "Geburtsdatum", "Firma"],
    zeitraum: true,
    beurteilung: true,
    spalten: [
      { key: "externe_mitarbeiter_id", label: "Externe Mitarbeiter-ID", keys: ["externe_mitarbeiter_id"], checked: false },
      { key: "bg_grundsatz", label: "BG Grundsatz", keys: ["bg_grundsatz"], checked: true },
      { key: "bezeichnung", label: "Bezeichnung / Bereich", keys: ["bezeichnung", "titel"], checked: true },
      { key: "letzte_untersuchung", label: "Letzte Untersuchung", keys: ["letzte_untersuchung"], checked: false },
      { key: "naechster_termin", label: "Nächster Termin", keys: ["naechster_termin"], checked: true },
      { key: "beurteilung", label: "Beurteilung", keys: ["beurteilung"], checked: true },
      { key: "kommentar", label: "Kommentar", keys: ["kommentar"], checked: false },
      // art (EU/NU) bleibt im Export (Verhaltens-Kontinuität, Klärung Nr. 6)
      { key: "art", label: "Art (EU/NU)", keys: ["art"], checked: true },
    ],
  },
  // Mitarbeiter-Liste (#mitarbeiter): Stammspalten, kein Termin-Datenbestand.
  // metaVorbefuellenAnsicht: die im Dialog angebotenen Metafelder starten
  // mit der Auswahl der persönlichen Tabellenspalten (Bestands-Parität —
  // der alte Direkt-Download exportierte genau diese) — Nutzeränderung
  // persistiert wie immer in localStorage
  mitarbeiter: {
    feste: ["Nachname", "Vorname", "Geburtstag"],
    zeitraum: false,
    beurteilung: false,
    metaVorbefuellenAnsicht: true,
    spalten: [
      { key: "zuordnungs", label: "Tätigkeit/Bereich (Kommentar)", keys: ["zuordnungs"], checked: true },
      { key: "email", label: "E-Mail", keys: ["email"], checked: true },
      { key: "externe_mitarbeiter_id", label: "Externe Mitarbeiter-ID", keys: ["externe_mitarbeiter_id"], checked: true },
    ],
  },
};

const PRESETS = [
  { id: "heute", label: "Nur heute fällig" },
  { id: "p4", label: "Heute + 4 Wochen" },
  { id: "p6", label: "Heute + 6 Wochen" },
  { id: "custom", label: "Eigener Zeitraum" },
];

const DEFAULT_AUSWAHL = {
  preset: "p6",
  customVon: "",
  customBis: "",
  // „Überfällige ausschließen“ als Haken: Default an, Überfällige fehlen
  // im Export — unverändertes Bestandsverhalten
  ueberfaelligeAusschliessen: true,
  includeNoTerm: false,
  columns: {},
  beurteilungen: [],
  metaKeys: [],
};

function readAuswahl(speicherKey) {
  try {
    const parsed = JSON.parse(localStorage.getItem(speicherKey));
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function writeAuswahl(speicherKey, auswahl) {
  try {
    localStorage.setItem(speicherKey, JSON.stringify(auswahl));
  } catch {
    // Quota/Privacy-Mode: Auswahl gilt nur für die Session
  }
}

function customZeitraumFehler(auswahl) {
  if (auswahl.preset !== "custom") return null;
  if (!auswahl.customVon || !auswahl.customBis) {
    return "Von und Bis sind Pflichtfelder.";
  }
  if (auswahl.customVon > auswahl.customBis) {
    return "Von liegt nach Bis.";
  }
  return null;
}

const CsvExportDialog = ({ isOpen, apiEndpoint, tabellenQuery, onClose }) => {
  const konfig = ENDPUNKT_KONFIG[apiEndpoint];
  const speicherKey = SPEICHER_PREFIX + apiEndpoint;
  const [auswahl, setAuswahl] = useState(null);
  const [metaFelder, setMetaFelder] = useState([]);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState(null);

  useEffect(() => {
    if (!isOpen || !konfig) return undefined;
    // zuletzt gewählte Auswahl, sonst Defaults; ohne Zeitraum-Sektion
    // (Mitarbeiter-Liste) darf kein Preset stehen, sonst ginge ein
    // from/to-Fenster versehentlich mit. Metafelder: ohne Speicherstand
    // aus der Tabellenansicht vorbelegen (Bestands-Parität), sobald der
    // Nutzer schaltet, gilt seine gespeicherte Auswahl
    const basis = konfig.zeitraum ? DEFAULT_AUSWAHL : { ...DEFAULT_AUSWAHL, preset: null };
    const gespeichert = readAuswahl(speicherKey);
    // Altstand vor dem Semantik-Flip („ueberfaellige“):
    // Bedeutung drehte sich um — ablegen lassen statt übernehmen
    if (gespeichert) delete gespeichert.ueberfaellige;
    const ohneMetaStand =
      !gespeichert ||
      !Array.isArray(gespeichert.metaKeys);
    const metaAnsicht = String(tabellenQuery?.metaCols ?? "")
      .split(",")
      .map((k) => k.trim())
      .filter(Boolean);
    setAuswahl({
      ...basis,
      ...(gespeichert || {}),
      metaKeys: konfig.metaVorbefuellenAnsicht && ohneMetaStand
        ? [...new Set(metaAnsicht)]
        : (gespeichert?.metaKeys ?? []),
    });
    let aktiv = true;
    (async () => {
      try {
        const response = await fetchApi("metaSettings");
        const felder = response && Array.isArray(response.data) ? response.data : [];
        if (aktiv) setMetaFelder(dedupeMetaFields(felder));
      } catch (err) {
        // Erweiterte Felder bleiben leer, statt den Export zu blockieren
        console.error("Fehler beim Laden der Metafelder für den Export:", err);
      }
    })();
    return () => {
      aktiv = false;
    };
  }, [isOpen, speicherKey]);

  useEffect(() => {
    if (auswahl) writeAuswahl(speicherKey, auswahl);
  }, [auswahl, speicherKey]);

  if (!isOpen || !auswahl || !konfig) return null;

  const columnsAuswahl = { ...auswahl.columns };
  // Effektiver Check-Status: Defaults (konfig.spalten.checked) gelten,
  // bis der Nutzer sie übersteuert; UI und Payload lesen dieselbe Quelle
  const effektivChecked = (option) =>
    columnsAuswahl[option.key] === undefined
      ? option.checked
      : columnsAuswahl[option.key] === true;
  const fieldsExport = [];
  for (const option of konfig.spalten) {
    if (effektivChecked(option)) fieldsExport.push(...option.keys);
  }
  const zeitraumFehler = customZeitraumFehler(auswahl);

  const aendern = (partial) => setAuswahl((prev) => ({ ...prev, ...partial }));

  const umschalten = (key) => {
    const option = konfig.spalten.find((o) => o.key === key);
    aendern({
      columns: { ...columnsAuswahl, [key]: !effektivChecked(option) },
    });
  };

  const chipUmschalten = (wert) => {
    const hat = auswahl.beurteilungen.includes(wert);
    aendern({
      beurteilungen: hat
        ? auswahl.beurteilungen.filter((b) => b !== wert)
        : [...auswahl.beurteilungen, wert],
    });
  };

  const metaUmschalten = (key) => {
    const hat = auswahl.metaKeys.includes(key);
    aendern({
      metaKeys: hat
        ? auswahl.metaKeys.filter((k) => k !== key)
        : [...auswahl.metaKeys, key],
    });
  };

  const exportieren = async () => {
    const zeitraumFehlerAktiv = customZeitraumFehler(auswahl);
    if (zeitraumFehlerAktiv) return;
    setFehler(null);
    const query = { ...(tabellenQuery || {}) };
    query.fields = fieldsExport.join(",");
    if (konfig.beurteilung && auswahl.beurteilungen.length > 0) {
      query.status = auswahl.beurteilungen.join(",");
    }
    const zeitraum = zeitraumVonPreset(auswahl) || {
      from: auswahl.customVon,
      to: auswahl.customBis,
    };
    Object.assign(query, zeitraumQueryFenster(zeitraum));
    if (auswahl.includeNoTerm) query.includeNoTerm = "1";
    // Der Dialog ist die alleinige Quelle für metaCols — eine alte
    // Tabellenansicht in tabellenQuery darf nicht durchsickern
    query.metaCols = auswahl.metaKeys.join(",");

    setLaeuft(true);
    try {
      await downloadCsv(apiEndpoint, query);
      onClose();
    } catch (err) {
      setFehler(err.message);
      toast.error("CSV-Export fehlgeschlagen: " + err.message);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <>
      <div
        className={"modal fade show"}
        id="modal-csv-export"
        aria-modal="true"
        role="dialog"
        aria-hidden="false"
        style={{ paddingRight: 15, display: "block" }}
      >
        <div className="modal-dialog modal-dialog-scrollable modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">
                <BsFileEarmarkArrowDown
                  fontSize={16}
                  style={{ marginRight: "8px" }}
                />
                CSV-Export
              </h4>
              <button
                type="button"
                className="close"
                aria-label="Close"
                onClick={onClose}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body">
              <UncontrolledAccordion
                stayOpen
                defaultOpen={["zeitraum", "spalten", "beurteilung", "metafelder"]}
              >
                {konfig.zeitraum && (
                  <AccordionItem>
                    <AccordionHeader targetId="zeitraum">
                      Fälligkeits-Zeitraum{" "}
                      <i className="icon">
                        <GoChevronDown />
                      </i>
                    </AccordionHeader>
                    <AccordionBody accordionId="zeitraum">
                      <div className="mb-1">
                        <CustomCheckbox
                          checked={auswahl.ueberfaelligeAusschliessen === true}
                          disabled={auswahl.preset === "custom"}
                          onChange={() =>
                            aendern({
                              ueberfaelligeAusschliessen: !(auswahl.ueberfaelligeAusschliessen === true),
                            })
                          }
                          name="csvExportUeberfaellige"
                          label="Überfällige ausschließen"
                        />
                      </div>
                      <FormText className="mb-2 d-block">
                        Mit dem Haken startet jedes Preset bei heute — Personen
                        mit Frist vor heute (überfällig) fehlen im Export;
                        ohne Haken wandern sie mit. Bei „Eigener Zeitraum“
                        sind Von/Bis maßgeblich.
                      </FormText>
                      <div className="mb-3">
                        {PRESETS.map((preset) => (
                          <Label

                            check

                            key={preset.id}
                            className="csv-radio-row d-block mb-1"

                          >

                            <Input

                              type="radio"

                              name="csvExportPreset"
                              value={preset.id}
                              checked={auswahl.preset === preset.id}
                              onChange={() => aendern({ preset: preset.id })}
                            />
                            {preset.label}
                          </Label>
                        ))}
                      </div>
                      {auswahl.preset === "custom" && (
                        <>
                          <div className="form-group">
                            <label htmlFor="csvExportVon" className="control-label">
                              Von
                            </label>
                            <Input
                              type="date"
                              id="csvExportVon"
                              value={auswahl.customVon || ""}
                              onChange={(e) => aendern({ customVon: e.target.value })}
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="csvExportBis" className="control-label">
                              Bis
                            </label>
                            <Input
                              type="date"
                              id="csvExportBis"
                              value={auswahl.customBis || ""}
                              onChange={(e) => aendern({ customBis: e.target.value })}
                            />
                          </div>
                          {zeitraumFehler && (
                            <div className="text-danger small">{zeitraumFehler}</div>
                          )}
                        </>
                      )}
                      <div className="mb-1">
                        <CustomCheckbox
                          checked={auswahl.includeNoTerm === true}
                          onChange={() => aendern({ includeNoTerm: !auswahl.includeNoTerm })}
                          name="csvExportOhneErgebnis"
                          label="Personen ohne Ergebnis"
                        />
                      </div>
                    </AccordionBody>
                  </AccordionItem>
                )}

                <AccordionItem>
                  <AccordionHeader targetId="spalten">
                    Spalten{" "}
                    <i className="icon">
                      <GoChevronDown />
                    </i>
                  </AccordionHeader>
                  <AccordionBody accordionId="spalten">
                    <FormText className="mb-2 d-block">
                      Feste Felder: {konfig.feste.join(", ")} — immer enthalten.
                    </FormText>
                    {konfig.spalten.map((option) => (
                      <div key={option.key} className="mb-1">
                        <CustomCheckbox
                          checked={effektivChecked(option)}
                          onChange={() => umschalten(option.key)}
                          name={"csvExportSpalte_" + option.key}
                          label={option.label}
                        />
                      </div>
                    ))}
                  </AccordionBody>
                </AccordionItem>

                {konfig.beurteilung && (
                  <AccordionItem>
                    <AccordionHeader targetId="beurteilung">
                      Beurteilung filtern{" "}
                      <i className="icon">
                        <GoChevronDown />
                      </i>
                    </AccordionHeader>
                    <AccordionBody accordionId="beurteilung">
                      <FormText className="mb-2 d-block">
                        Mehrfachauswahl möglich; ohne Auswahl exportiert alle
                        mit Ergebnis.
                      </FormText>
                      <div className="d-flex flex-wrap">
                        {BEURTEILUNGEN.map((wert) => (
                          <button
                            key={wert}
                            type="button"
                            className={
                              "btn btn-sm m-1 " +
                              (auswahl.beurteilungen.includes(wert)
                                ? "btn-primary"
                                : "btn-outline")
                            }
                            onClick={() => chipUmschalten(wert)}
                          >
                            {wert}
                          </button>
                        ))}
                      </div>
                    </AccordionBody>
                  </AccordionItem>
                )}

                <AccordionItem>
                  <AccordionHeader targetId="metafelder">
                    Erweiterte Felder{" "}
                    <i className="icon">
                      <GoChevronDown />
                    </i>
                  </AccordionHeader>
                  <AccordionBody accordionId="metafelder">
                    {metaFelder.length > 0 ? (
                      metaFelder.map((feld) => (
                        <div key={feld.key} className="mb-1">
                          <CustomCheckbox
                            checked={auswahl.metaKeys.includes(feld.key)}
                            onChange={() => metaUmschalten(feld.key)}
                            name={"csvExportMeta_" + feld.key}
                            label={feld.label}
                          />
                        </div>
                      ))
                    ) : (
                      <FormText>Keine Metafelder definiert.</FormText>
                    )}
                  </AccordionBody>
                </AccordionItem>
              </UncontrolledAccordion>

              {fehler && (
                <div className="alert alert-danger mt-3 mb-0 small">{fehler}</div>
              )}
            </div>
            <div className="modal-footer">
              <div />
              <div>
                <button type="button" className="btn btn-outline" onClick={onClose}>
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={exportieren}
                  disabled={zeitraumFehler !== null || laeuft}
                >
                  {laeuft ? "Exportiere..." : "CSV exportieren"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
    </>
  );
};

export default CsvExportDialog;